/**
 * features/payments/RecordPaymentModal.tsx
 * Record a payment against a single invoice. Amount defaults to the
 * outstanding balance and is capped to it (overpayment would need a
 * credit note, a later feature).
 */
import { useState } from 'react';
import { X, Receipt } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { formatPula, round2 } from '@/lib/money';
import { glassSurface } from '@/components/ui/glass';
import { Button, Field, Input, Select, Textarea } from '@/components/ui/primitives';
import type { Invoice, PaymentMethod } from '@/types';
import { recordPayment } from './paymentsWrite';

const METHODS: Array<{ value: PaymentMethod; label: string }> = [
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'cash', label: 'Cash' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'card', label: 'Card' },
  { value: 'mobile', label: 'Mobile money' },
  { value: 'other', label: 'Other' },
];

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export function RecordPaymentModal({
  invoice,
  onClose,
  onRecorded,
}: {
  invoice: Invoice;
  onClose: () => void;
  onRecorded?: (receiptNumber: string) => void;
}) {
  const { user } = useAuth();
  const balance = round2(invoice.balance);
  const [amount, setAmount] = useState(String(balance));
  const [date, setDate] = useState(todayISO());
  const [method, setMethod] = useState<PaymentMethod>('bank_transfer');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [receipt, setReceipt] = useState('');

  const amt = Number(amount) || 0;
  const valid = amt > 0 && amt <= balance + 0.001;

  async function save() {
    if (!valid) {
      setError(amt <= 0 ? 'Enter an amount greater than zero.' : `Amount cannot exceed the ${formatPula(balance)} balance.`);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await recordPayment(
        invoice,
        { amount: round2(amt), date, method, reference, notes },
        user?.email ?? user?.uid ?? 'unknown',
      );
      setReceipt(res.receiptNumber);
      onRecorded?.(res.receiptNumber);
      setTimeout(onClose, 1100);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not record the payment.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-md">
      <div className="my-12 w-full max-w-md">
        <div className={glassSurface('space-y-4 p-5')}>
          <div className="-mx-5 -mt-5 mb-1 flex items-center justify-between rounded-t-card bg-brand px-5 py-3.5">
            <div className="flex items-center gap-2 text-white">
              <Receipt size={18} />
              <h2 className="text-base font-semibold text-white">Record payment</h2>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-1 text-white/80 transition hover:bg-white/15 hover:text-white"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          <div className="rounded-xl bg-brandSoft px-4 py-3 text-sm dark:bg-white/5">
            <div className="flex justify-between text-primaryLight dark:text-gray-300">
              <span>Invoice {invoice.number}</span>
              <span>{invoice.client.name}</span>
            </div>
            <div className="mt-1 flex justify-between font-semibold text-primary dark:text-gray-100">
              <span>Outstanding</span>
              <span>{formatPula(balance)}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount">
              <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className="text-right" />
            </Field>
            <Field label="Date">
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </Field>
            <Field label="Method">
              <Select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
                {METHODS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Reference" hint="Cheque / txn no.">
              <Input value={reference} onChange={(e) => setReference(e.target.value)} />
            </Field>
          </div>

          <Field label="Notes">
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          {receipt && (
            <p className="text-sm font-semibold text-green-600 dark:text-green-400">
              Recorded ✓ Receipt {receipt}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={save} loading={busy} disabled={!!receipt}>
              Record {amt > 0 ? formatPula(round2(amt)) : 'payment'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
